/** @jsx jsx */
import { Modal, Spacer } from "@cd2/components"
import { Fields } from "@cd2/forms"
import { H3, P } from "@cd2/text"
import { css, jsx } from "@emotion/core"
import { useState } from "react"
import { useHistory } from "react-router-dom"
import { IProductLocationStockResponseItem, useApi } from "../hooks/useApi"
import { useAsyncCallback } from "../hooks/useAsyncCallback"
import { useConfirmation } from "../hooks/useConfirmation"
import { useOrderRequestDefaultSettings } from "../hooks/useOrderRequestDefaultSettings"
import {
  getStockWarehouseIdsForBranch,
  IItem,
  IOrderRequest,
  IProductItem,
  ITargetBranchProduct,
  ITextItem,
  ORDER_REQUEST_ITEM_NEW_ID_PREFIX,
} from "../Pages/OrderRequests/OrderRequestsStandaloneForm"
import { IRecord } from "../types/schema"
import { ConfirmCancelButtons } from "./Buttons/ConfirmCancelButtons"
import { FieldWithDescription } from "./FieldWithDescription"
import { assert, isInternalOrder } from "../utils/utils"
import { IBranchSettings } from "../utils/useSettings"
import { egsBranchId } from "../../shared/constants"

export const prefixAddressAttributes = [
  "label",
  "name",
  "line_1",
  "line_2",
  "line_3",
  "line_4",
  "city",
  "county",
  "country",
  "postcode",
  "contact",
  "email",
  "phone",
] as const

export function prefixAddress(prefix: string, address?: Partial<IAddress>) {
  return {
    [`${prefix ? `${prefix}_` : ""}address_city`]: address?.city ?? "",
    [`${prefix ? `${prefix}_` : ""}address_country`]: address?.country ?? "",
    [`${prefix ? `${prefix}_` : ""}address_county`]: address?.county ?? "",
    [`${prefix ? `${prefix}_` : ""}address_email`]: address?.email ?? "",
    [`${prefix ? `${prefix}_` : ""}address_line_1`]: address?.line_1 ?? "",
    [`${prefix ? `${prefix}_` : ""}address_line_2`]: address?.line_2 ?? "",
    [`${prefix ? `${prefix}_` : ""}address_line_3`]: address?.line_3 ?? "",
    [`${prefix ? `${prefix}_` : ""}address_line_4`]: address?.line_4 ?? "",
    [`${prefix ? `${prefix}_` : ""}address_name`]: address?.name ?? "",
    [`${prefix ? `${prefix}_` : ""}address_phone`]: address?.phone ?? "",
    [`${prefix ? `${prefix}_` : ""}address_postcode`]: address?.postcode ?? "",
  }
}

type IAddress = IRecord<"addresses", (typeof prefixAddressAttributes)[number]>

type ILoadedOrderForOrderRequest = IRecord<
  "orders",
  | "internal_transfer_to_branch"
  | "barScanPartShip"
  | "identifier"
  | "will_collect"
  | "hasOpportunityCertificate"
  | "carriage_pennies"
> & {
  customer: IRecord<"customers", "name">
  dpdProduct?: IRecord<"products", "body" | "sale_price_pennies">
  address?: IAddress
  billing_address?: IAddress
  branch: IRecord<"branches", "name">
  purchaseOrders: (IRecord<"purchase_orders"> & {
    order_request: IRecord<"orderRequests", "ship_to_branch"> | null
  })[]
  items: (IRecord<
    "order_items",
    | "type"
    | "quantity"
    | "body"
    | "weight"
    | "discountPercent"
    | "price_pennies"
    | "cost_price_pennies"
    | "opportunity_certificate_cost_discount_amount_pennies"
  > & {
    product?: IRecord<"products", "stock_code" | "body" | "cost_price_pennies" | "sage_id"> & {
      product_locations: IRecord<"product_locations", "name" | "warehouse" | "free_stock">[]
      suppliers: IRecord<"suppliers", "name">
      preferred_supplier?: IRecord<"suppliers", "name">
    }
    product_location: IRecord<"product_locations", "name">
  })[]
}

type IOrderRequestBuildOptions = {
  onlyShortfall: boolean
  includeComments: boolean
}

type IOrderRequestTargetBranch = {
  id: string
}

type IOrderRequestBranchWarehouseContext = {
  sourceBranchId: string
  targetBranchId: string
  targetBranchWarehouses: string[]
  allWarehouses: string[]
}

type IOrderRequestSourceProduct = IRecord<
  "products",
  "stock_code" | "body" | "product_master" | "product_locations"
>

export function useOrderRequestGenerator() {
  console.log("useOrderRequestGenerator")
  const skeem = window.skeem
  const history = useHistory()
  const api = useApi()
  const { data: defaultOrderRequestBranch } = useOrderRequestDefaultSettings()

  function loadOrder(orderId: string) {
    return skeem.fetch2<ILoadedOrderForOrderRequest>({
      first: {
        name: "orders",
        attributes: [
          "carriage_pennies",
          "hasOpportunityCertificate",
          "delivery_method",
          "will_collect",
          "identifier",
          "internal_transfer_to_branch",
          { name: "customer", attributes: ["name"] },
          "barScanPartShip",
          { name: "dpdProduct", attributes: ["body", "sale_price_pennies"] },
          { name: "branch", attributes: ["name"] },
          { name: "address", attributes: prefixAddressAttributes },
          { name: "billing_address", attributes: prefixAddressAttributes },
          {
            name: "purchaseOrders",
            attributes: [{ name: "order_request", attributes: ["ship_to_branch"] }],
          },
          {
            name: "items",
            attributes: [
              "type",
              "quantity",
              "body",
              "weight",
              "discountPercent",
              "price_pennies",
              "cost_price_pennies",
              "opportunity_certificate_cost_discount_amount_pennies",
              {
                name: "product",
                attributes: [
                  "stock_code",
                  "body",
                  "cost_price_pennies",
                  "sage_id",
                  { name: "product_locations", attributes: ["name", "warehouse", "free_stock"] },
                  { name: "suppliers", attributes: ["name"] },
                  { name: "preferred_supplier", attributes: ["name"] },
                ],
              },
              {
                name: "product_location",
                attributes: [
                  "name",
                  {
                    name: "order_items",
                    attributes: ["quantity", { name: "order", attributes: ["identifier"] }],
                    filter: { matches: { order: { not: null } } },
                  },
                ],
              },
            ],
            sort: { by: "weight", direction: "asc" },
          },
        ],
        matches: { id: orderId },
      },
    })
  }

  const [optionsModal, handleGetOptions] = useConfirmation<
    void,
    null | { onlyShortfall: boolean; includeComments: boolean }
  >(({ onResolve }) => {
    const [shortfallOnly, setShortfallOnly] = useState(true)
    const [includeCommentLines, setIncludeCommentLines] = useState(true)
    return (
      <Modal
        onRequestClose={() => onResolve(null)}
        render={() => (
          <div>
            <H3>Generate order request for this order</H3>
            <P>
              This will build the order request for the order items. You can review the order
              request on the next stage.
            </P>
            <Spacer />
            <FieldWithDescription
              description="With this checked, only items that have a shortfall will be put on the generated Order request, if you wish to create an OR for this order in its entirety, uncheck this box."
              checkbox
            >
              <div
                css={css`
                  display: flex;
                  justify-content: left !important;
                  align-items: center;
                `}
              >
                <Fields.Checkbox
                  label="Only include items with shortfalls"
                  value={shortfallOnly}
                  onChange={setShortfallOnly}
                />
              </div>
            </FieldWithDescription>
            <Spacer />
            <FieldWithDescription
              description="With this checked, comment lines will also be copied across to the new order request."
              checkbox
            >
              <div
                css={css`
                  display: flex;
                  justify-content: left !important;
                  align-items: center;
                `}
              >
                <Fields.Checkbox
                  label="Include comment lines"
                  value={includeCommentLines}
                  onChange={setIncludeCommentLines}
                />
              </div>
            </FieldWithDescription>
            <Spacer />

            <ConfirmCancelButtons
              onConfirm={() =>
                onResolve({ onlyShortfall: shortfallOnly, includeComments: includeCommentLines })
              }
              onCancel={() => onResolve(null)}
            />
          </div>
        )}
        isOpen
      />
    )
  })

  async function generateOrderRequest(orderId: string, opts: { directToCustomer?: boolean } = {}) {
    if (!defaultOrderRequestBranch?.id) {
      console.error("No default order request branch")
      throw new Error("No default order request branch")
    }
    const order = await loadOrder(orderId)

    // Check if there's already a direct to customer order request
    if (
      opts.directToCustomer &&
      order.purchaseOrders.some(po => po.order_request && !po.order_request?.ship_to_branch)
    ) {
      throw new Error("Order already has a Direct to Customer Order Request")
    }

    let options: {
      onlyShortfall: boolean
      includeComments: boolean
    } = {
      onlyShortfall: false,
      includeComments: true,
    }
    if (!opts.directToCustomer) {
      const optionsResult = await handleGetOptions()
      if (!optionsResult) {
        return
      }
      options = optionsResult
    }

    const items = await buildOrderRequestItemsFromOrder(order, {
      options,
      targetBranch: defaultOrderRequestBranch,
      directToCustomer: opts.directToCustomer,
      productLocationsStock: productLocationIds => api.productLocationsStock(productLocationIds),
    })

    const carriagePennies = opts.directToCustomer ? order.carriage_pennies || undefined : undefined

    const orderRequestData: Partial<IOrderRequest> = {
      ship_to_branch: !opts.directToCustomer,
      items: items,
      sourceOrder: order,
      carriage_pennies: carriagePennies,
      shipping_address_name: order.address?.name,
      shipping_address_line_1: order.address?.line_1,
      shipping_address_line_2: order.address?.line_2,
      shipping_address_line_3: order.address?.line_3,
      shipping_address_line_4: order.address?.line_4,
      shipping_address_city: order.address?.city,
      shipping_address_county: order.address?.county,
      shipping_address_country: order.address?.country,
      shipping_address_postcode: order.address?.postcode,
      shipping_address_phone: order.address?.phone,
      shipping_address_email: order.address?.email,
    }

    if (order.dpdProduct) {
      orderRequestData.dpdProduct = order.dpdProduct
    }

    history.push("/order-requests/new", { initialData: orderRequestData })
  }

  const func = useAsyncCallback(generateOrderRequest)
  ;(func as typeof func & { modal: React.ReactNode }).modal = optionsModal
  return func as typeof func & { modal: React.ReactNode }
}

/**
 * Build order-request line items from a loaded order.
 *
 * Phase 0 — branch context, isInternal, and source products by id (3 skeem fetches, shared)
 * Phase 1 — per order line: skip / text item / product item (1 skeem fetch per product line)
 * Phase 2 — optional shortfall filter (single batched stock API call)
 */
async function buildOrderRequestItemsFromOrder(
  order: ILoadedOrderForOrderRequest,
  {
    options,
    targetBranch,
    directToCustomer,
    productLocationsStock,
  }: {
    options: IOrderRequestBuildOptions
    targetBranch: IOrderRequestTargetBranch
    directToCustomer?: boolean
    productLocationsStock: (
      productLocationIds: string[],
    ) => Promise<Record<string, IProductLocationStockResponseItem>>
  },
): Promise<IItem[]> {
  let items: IItem[] = []

  const branches = await window.skeem.fetch2<
    { settings: IBranchSettings; id: string; warehouses: { id: string }[] }[]
  >({
    model: {
      name: "branches",
      attributes: [
        "settings",
        "warehouses",
      ],
      filter: { matches: { id: [order.branch.id, targetBranch.id] } },
    },
  })

  const sourceBranch = branches.find(b => b.id === order.branch.id)
  const targetBranchRecord = branches.find(b => b.id === targetBranch.id)

  assert(sourceBranch, "Source branch not found")
  assert(targetBranchRecord, "Target branch not found")

  const sourceBranchWarehouses = getStockWarehouseIdsForBranch(sourceBranch)
  const targetBranchWarehouses = getStockWarehouseIdsForBranch(targetBranchRecord)

  const branchWarehouseContext: IOrderRequestBranchWarehouseContext = {
    sourceBranchId: order.branch.id,
    targetBranchId: targetBranch.id,
    targetBranchWarehouses,
    allWarehouses: Array.from(new Set([...sourceBranchWarehouses, ...targetBranchWarehouses])),
  }

  const isInternal = await isInternalOrder(order.branch.id, targetBranch.id)

  const productIds = [
    ...new Set(
      order.items.filter(item => item.type !== 1 && item.product?.id).map(item => item.product!.id),
    ),
  ]

  const sourceProductsById: Record<string, IOrderRequestSourceProduct> = {}
  if (productIds.length > 0) {
    const sourceProducts = await window.skeem.fetch2<IOrderRequestSourceProduct[]>({
      model: {
        name: "products",
        attributes: ["stock_code", "body", "product_master", "product_locations"],
        filter: { matches: { id: { or: productIds } } },
      },
    })
    for (const sourceProduct of sourceProducts) {
      sourceProductsById[sourceProduct.id] = sourceProduct
    }
  }

  // --- Phase 1: map each order line to an order-request item ---
  for (const item of order.items) {
    // Skip comment lines when the user opted out of including comments
    if (item.type == 1 && !options.includeComments) {
      continue
    }

    // Comment line → copy across as a text item (no network)
    if (item.type === 1) {
      const textItem: ITextItem = {
        id: `${ORDER_REQUEST_ITEM_NEW_ID_PREFIX}-${item.id}`,
        body: item.body,
        type: "text",
        commentLine: true,
      }
      items.push(textItem)
      continue
    }

    // Product line → load cross-branch pricing and alternative data (1 skeem fetch per line)
    assert(item.product, "Order item has no product")

    const sourceProduct = sourceProductsById[item.product.id]
    assert(sourceProduct, "Order item product not found")

    const { product, sourceBranchData, targetBranchData, alternative, targetBranchPrice } =
      await handleLoadOrderRequestProductData({
        product: sourceProduct,
        branchWarehouseContext,
        isInternal,
      })

    // Resolve target price; direct-to-customer internal orders keep the order line price
    let targetPrice = targetBranchPrice ?? null
    if (directToCustomer && isInternal) {
      targetPrice = item.price_pennies
    }

    // Assemble the product item from the order line plus fetched branch data
    const productItem: IProductItem = {
      ...item,
      id: `${ORDER_REQUEST_ITEM_NEW_ID_PREFIX}-${item.id}`,
      stock_code: product.stock_code,
      body: product.body,
      target_cost_price_pennies: targetBranchData?.cost_price_pennies,
      source_cost_price_pennies: sourceBranchData?.cost_price_pennies,
      target_product: targetBranchData,
      source_product: sourceBranchData,
      price_pennies: targetPrice,
      discountPercent: item.discountPercent,
      alternative,
      type: "product",
    }

    items.push(productItem)
  }

  // --- Phase 2: optional shortfall filter ---
  // When enabled, fetch stock for all product locations on the order (1 batched API call),
  // then drop product lines that already have sufficient stock at their allocation.
  if (options.onlyShortfall) {
    const productLocationIds = new Set(
      order.items.filter(i => !!i.product_location).map(i => i.product_location.id) || [],
    )
    const stock = await productLocationsStock(Array.from(productLocationIds))

    items = items.filter(i => {
      if (i.type !== "product") return true
      const item = order.items.find(
        item => i.id === `${ORDER_REQUEST_ITEM_NEW_ID_PREFIX}-${item.id}`,
      )
      if (!item || !item.product_location || !stock[item.product_location.id]) return true
      const stockItem = stock[item.product_location.id]
      const stockRequired = item.quantity ?? 1
      const isAvailable = stockItem.inStock >= stockRequired
      stockItem.inStock -= stockRequired
      return !isAvailable
    })
  }

  return items
}

async function handleLoadOrderRequestProductData(opts: {
  product: IOrderRequestSourceProduct
  branchWarehouseContext: IOrderRequestBranchWarehouseContext
  isInternal: boolean
}) {
  const { sourceBranchId, targetBranchId, targetBranchWarehouses, allWarehouses } =
    opts.branchWarehouseContext
  const product = opts.product

  const targetProductAttributes = [
    "stock_code",
    "body",
    "preferred_supplier_list_price_pennies",
    "cost_price_pennies",
    "product_master",
    { name: "branch", attributes: ["name"] },
    { name: "product_group", attributes: ["preventOrderRequest"] },
    {
      name: "product_locations",
      attributes: ["warehouse"],
      filter: { matches: { warehouse: allWarehouses } },
    },
  ]

  const products = await window.skeem.fetch2<ITargetBranchProduct[]>({
    model: {
      name: "products",
      attributes: [
        ...targetProductAttributes,
        {
          name: "orderRequestAlternativeProduct",
          attributes: [
            ...targetProductAttributes,
            {
              as: "price_band_products",
              value: {
                path: [
                  "product_master",
                  {
                    name: "universal_price_band_products",
                    attributes: [
                      "price_pennies",
                      { as: "price_band_name", value: { path: ["universal_price_band", "name"] } },
                    ],
                    filter: {
                      gte: [
                        {
                          count: {
                            path: [
                              {
                                name: "universal_price_band",
                                filter: { matches: { name: { or: ["BRANCHES", "BRANCHES-EGS"] } } },
                              },
                            ],
                          },
                        },
                        { value: 1 },
                      ],
                    },
                  },
                ],
              },
            },
          ],
        },
        {
          as: "price_band_products",
          value: {
            path: [
              "product_master",
              {
                name: "universal_price_band_products",
                attributes: [
                  "price_pennies",
                  { as: "price_band_name", value: { path: ["universal_price_band", "name"] } },
                ],
                filter: {
                  gte: [
                    {
                      count: {
                        path: [
                          {
                            name: "universal_price_band",
                            filter: { matches: { name: { or: ["BRANCHES", "BRANCHES-EGS"] } } },
                          },
                        ],
                      },
                    },
                    { value: 1 },
                  ],
                },
              },
            ],
          },
        },
      ],
      filter: {
        eq: [{ lowercase: { attr: "stock_code" } }, { value: product.stock_code.toLowerCase() }],
      },
    },
  })

  // for each of the products, we want to strip out the non-relevant UPBs. if branch is EGS
  // - only use the BRANCHES-EGS ones, otherwise use the BRANCHES ones
  const upbName = sourceBranchId === egsBranchId ? "BRANCHES-EGS" : "BRANCHES"
  products.forEach(product => {
    if (product.orderRequestAlternativeProduct) {
      product.orderRequestAlternativeProduct.price_band_products =
        product.orderRequestAlternativeProduct.price_band_products.filter(
          x => x.price_band_name === upbName,
        )
    }
    product.price_band_products = product.price_band_products.filter(
      x => x.price_band_name === upbName,
    )
  })

  const sourceBranchData = products.find(p => p.id === product.id && p.branch.id === sourceBranchId)

  const targetBranchData =
    sourceBranchData?.orderRequestAlternativeProduct ??
    products.find(p => p.branch.id === targetBranchId)

  if (targetBranchData) {
    // strip out product locations not in target branch warehouses as we can now have the same source/target branch
    targetBranchData.product_locations = targetBranchData.product_locations.filter(
      pl => pl.warehouse && targetBranchWarehouses.includes(pl.warehouse.id),
    )
  }

  const productItem = sourceBranchData?.orderRequestAlternativeProduct
    ? {
        id: sourceBranchData.orderRequestAlternativeProduct.id,
        body: sourceBranchData.orderRequestAlternativeProduct.body,
        stock_code: sourceBranchData.orderRequestAlternativeProduct.stock_code,
        product_master: sourceBranchData.orderRequestAlternativeProduct.product_master,
      }
    : product

  let targetBranchPrice =
    targetBranchData?.cost_price_pennies || sourceBranchData?.cost_price_pennies

  const priceBandPrice =
    targetBranchData?.price_band_products?.[0] || sourceBranchData?.price_band_products?.[0]
  if (!opts.isInternal && priceBandPrice?.price_pennies) {
    targetBranchPrice = priceBandPrice.price_pennies
    console.log("Found target branch price from branches price band", targetBranchPrice)
  }
  return {
    product: productItem,
    sourceBranchData,
    targetBranchData,
    alternative: !!sourceBranchData?.orderRequestAlternativeProduct,
    targetBranchPrice,
  }
}
